import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import * as _ from 'underscore';
import { BaseModalComponent } from '../../../models/base-modal-component';
import { Corpus } from '../../../models/corpus';
import { MainEmailThreadDocument } from '../../../models/dr-document';
import { Endpoint, EntityType, HelpIndex, MessageType, ModalTargetType, TagOperationType, TagScope, TagState, UpdateStatusType } from '../../../models/enums';
import { ActionType, ObjectActionListType, Permissions } from '../../../models/permission';
import { Tag, TagApplication, TagDocumentsOperation } from '../../../models/tag';
import { TaggedEntity } from '../../../models/tagged-entity';
import { CommonService } from '../../../services/common/common.service';
import { RestService } from '../../../services/rest/rest.service';
import { EditBucketModalComponent, EditMode } from '../edit-bucket/edit-bucket-modal.component';
import { Task } from '../../../models/task';
import { ViewType } from '../../../models/view';
import { isNullOrUndefined } from '../../../models/utils';

export interface TagsModalData {
    operationType: TagOperationType;
    targetType: ModalTargetType;
    targets: Array<any>;
    updateStatusType?: UpdateStatusType;
    selectedTags?: Array<TagApplication>;
    workBasketTaskHandle?: Array<string>;
}

@Component({
    standalone: false,
    selector: 'res-tags-modal',
    templateUrl: './tags-modal.component.html',
    styleUrls: ['./tags-modal.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class TagsModalComponent extends BaseModalComponent implements OnInit {
    readonly TagScope = TagScope;

    operationType: TagOperationType;
    targetType: ModalTargetType;
    targets = [];
    updateStatusType: UpdateStatusType;
    verifyRequiredValidator = true;

    isBusy = false;
    isBusyMessage = '';
    allTags: Array<Tag>;
    filteredTags: Array<Tag>;

    newTagVisible: boolean;
    selectedTags?: Array<TagApplication>;

    scope: TagScope;
    scopeVisible: boolean;
    tooltipText = '';

    description: string;
    descriptionVisible: boolean;

    taskDescription = '';
    tagApplications = new Array<TagApplication>();
    modalTitle = '';

    modalAutomationId = 'tags';
    secondaryButtonLabel = 'Cancel';
    primaryButtonLabel = 'OK';
    modalClass = 'tags-modal';
    modalHelpIndex = HelpIndex.myedisc_select_tags_menu_dlg;

    documentScopeDisabled: boolean;
    familyScopeDisabled: boolean;
    threadScopeDisabled: boolean;

    // permissions
    hasCreateTagPermissions: boolean;
    scopeName: string;

    constructor(
        private modalService: BsModalService,
        private restService: RestService,
        private commonService: CommonService,
        private modalRef: BsModalRef,
    ) {
        super(modalService, modalRef);
    }

    ngOnInit(): void {
        const tagsModalData = this.modalData as TagsModalData;
        if (tagsModalData) {
            this.operationType = tagsModalData.operationType;
            if (this.operationType !== TagOperationType.select && this.operationType !== TagOperationType.selectTagStateList) {
                this.targetType = tagsModalData.targetType;
                this.targets = tagsModalData.targets;
                this.updateStatusType = tagsModalData.updateStatusType;

                if (tagsModalData.targets && tagsModalData.targets[0] instanceof Corpus) {
                    this.tooltipText = 'Add selected tags AND add the documents to Project Data';
                }
                this.modalTitle = this.operationType === TagOperationType.tag ? 'Add Tags' : 'Remove Tags';
                this.getTargetTypeInfo();
            } else {
                this.modalTitle = 'Select Tags';
                this.selectedTags = tagsModalData.selectedTags;
            }
        }

        this.newTagVisible = this.operationType !== TagOperationType.untag;
        this.listTags();

        this.hasCreateTagPermissions = Permissions.checkUserPermissions([{
            permissionType: ObjectActionListType.PRJ_TAGS,
            permissionActions: [ActionType.CREATE]
        }]);
    }

    getTargetTypeInfo(): void {
        switch (this.targetType) {
            case ModalTargetType.document: {
                this.taskDescription = 'Tagging ' + (this.targets.length > 1 ? 'documents' : 'document');
                this.scopeVisible = true;
                this.scope = TagScope.MESSAGE_ATTACHMENT_GROUP;
                this.descriptionVisible = true;
                break;
            }
            case ModalTargetType.emailThread: {
                this.scope = TagScope.EMAIL_THREAD;
                this.taskDescription = 'Tagging ' + (this.targets.length > 1 ? 'email threads' : 'email thread');
                this.scopeVisible = true;
                this.descriptionVisible = true;
                break;
            }
            case ModalTargetType.bulk:
            case ModalTargetType.view: {
                let tagTarget = this.modalData.workflowStepType ? this.modalData.workflowStepType : this.targets[0].name;
                if (this.targetType === ModalTargetType.bulk) {
                    this.taskDescription = this.operationType === TagOperationType.tag ? 'Tagging bulk search' :
                        'Removing tag from bulk search';
                } else if (!tagTarget && this.targets[0] instanceof Task) {
                    const viewtype = this.targets[0].viewType;
                    if (viewtype === ViewType.CORPUS_RESULT || viewtype === ViewType.CORPUS_SET_RESULT ||
                        viewtype === ViewType.WORKING_SET_RESULT) {
                        this.taskDescription = this.operationType === TagOperationType.tag ? 'Tagging search results' :
                            'Removing tag from search results';
                    }
                } else if (isNullOrUndefined(tagTarget) && this.targets[0] instanceof MainEmailThreadDocument) {
                    this.taskDescription = 'Tagging email threads';
                } else {
                    if (this.commonService.matrixGridsSelection?.openFromDocumentActionBtn) {
                        tagTarget = this.commonService.matrixGridsSelection?.viewName;
                    }
                    this.taskDescription = this.operationType === TagOperationType.tag ? 'Tagging view ' + (tagTarget) : 'Removing tag from ' + (tagTarget);
                }
                this.scopeVisible = true;
                this.scope = TagScope.DOCUMENT_ONLY;
                this.descriptionVisible = true;
                break;
            }
            case ModalTargetType.report: {
                const description = this.targets[0].viewName ? this.targets[0].viewName : this.targets[0].description;
                this.taskDescription = 'Tagging view ' + description;
                this.scopeVisible = false;
                this.descriptionVisible = true;
                break;
            }
        }
        this.documentScopeDisabled = this.scopeDisabled(TagScope.DOCUMENT_ONLY);
        this.familyScopeDisabled = this.scopeDisabled(TagScope.MESSAGE_ATTACHMENT_GROUP);
        this.threadScopeDisabled = this.scopeDisabled(TagScope.EMAIL_THREAD);
    }

    listTags(newTagResponse?: any): void {
        this.isBusy = true;
        this.isBusyMessage = 'Loading tags...';
        this.restService.post(Endpoint.workingSetManager_listWorkingSetViews, {
            workingSetHandle: this.commonService.projectData.handle,
            count: 0,
            startIndex: 0,
            filters: [{
                attribute: 'viewType',
                value: 'TAG',
                operator: 'EQUALS'
            }]
        }).then(response => {
            this.isBusy = false;
            if (response && response.views) {
                this.allTags = this.filteredTags = (<Array<any>>response.views).map(t => new Tag(t));
                if (this.operationType !== TagOperationType.untag) {
                    if (this.targetType === ModalTargetType.view && this.targets.length === 1) {
                        if (this.targets[0].taggedEntityList && this.allTags) {
                            _.each(this.allTags, (tag) => {
                                const tagItem = this.targets[0].taggedEntityList.find(taggedItem => {
                                    return taggedItem.handle === tag.handle;
                                });
                                if (tagItem) {
                                    tag.selected = true;
                                }
                            });
                        }
                    } else if ((this.operationType === TagOperationType.select || this.operationType === TagOperationType.selectTagStateList) &&
                        this.selectedTags && this.selectedTags.length > 0) {
                        _.each(this.allTags, (tag) => {
                            const tagItem = this.selectedTags.find(ta => {
                                return ta.tagHandle === tag.handle;
                            });
                            if (tagItem) {
                                tag.selected = true;
                                this.tagSelected(tag);
                            }
                        });
                    }
                    if (newTagResponse && newTagResponse.tag) {
                        _.each(this.tagApplications, (element) => {
                            const tag = this.allTags.find(t => t.handle === element.tagHandle);
                            if (tag) {
                                tag.selected = true;
                            }
                        });
                        _.each(this.allTags, (tag) => {
                            if (newTagResponse.tag.handle === tag.handle) {
                                tag.selected = true;
                                this.tagSelected(tag);
                            }
                        });
                    }
                }
            }
        }, error => {
            this.isBusy = false;
        });
    }

    // This method checks for checked/unchecked tags and pushes them into the tagApplications array
    tagSelected(tag: Tag): void {
        if (tag.selected) {
            const tagApplication = this.tagApplications.find(taggedApplication => {
                return taggedApplication.tagHandle === tag.handle;
            });
            if (!tagApplication) {
                if (this.operationType === TagOperationType.untag) {
                    this.tagApplications.push(new TagApplication({ tagHandle: tag.handle, tagState: TagState.NONE }));
                } else {
                    this.tagApplications.push(new TagApplication({ tagHandle: tag.handle, tagState: TagState.ON }));
                }
            }
        } else {
            const tagApplication = this.tagApplications.find(taggedApplication => {
                return taggedApplication.tagHandle === tag.handle;
            });
            if (tagApplication) {
                // existing ON application => remove
                const index = this.tagApplications.indexOf(tagApplication);
                if (index > -1) {
                    this.tagApplications.splice(index, 1);
                }
            } else {
                // new NONE application => add
                this.tagApplications.push(new TagApplication({ tagHandle: tag.handle, tagState: TagState.NONE }));
            }
        }
    }

    isPrimaryButtonEnabled(): boolean {
        return this.tagApplications.length > 0 && !this.isBusy;
    }

    scopeDisabled(scope: TagScope): boolean {
        if (this.targetType === ModalTargetType.document && this.modalData.parentType === EntityType.import) {
            this.scope = TagScope.DOCUMENT_ONLY;
            return scope !== TagScope.DOCUMENT_ONLY;
        } else if (this.targets[0] instanceof MainEmailThreadDocument) {
            this.scope = TagScope.EMAIL_THREAD;
            return scope !== TagScope.EMAIL_THREAD;
        } else if (this.targetType === ModalTargetType.view) {
            return scope !== TagScope.DOCUMENT_ONLY;
        } else if (this.targetType === ModalTargetType.bulk) {
            return scope !== TagScope.DOCUMENT_ONLY;
        }
        return false;
    }

    ok(): void {
        if (this.operationType === TagOperationType.select) {
            this.closeModal(true, this.tagApplications);
        } else if (this.operationType === TagOperationType.selectTagStateList) {
            const taggedEntityList: Array<TaggedEntity> = [];
            this.tagApplications.forEach(element => {
                const tag = this.allTags.find(t => t.handle === element.tagHandle);
                if (tag) {
                    taggedEntityList.push(new TaggedEntity({
                        handle: tag.handle,
                        name: tag.name,
                        tagState: element.tagState,
                        displayValue: tag.displayValue
                    }));
                }
            });
            this.closeModal(true, taggedEntityList);
        } else {
            let request = null;
            let endpoint: Endpoint;
            if ((this.targetType === ModalTargetType.view) || (this.targetType === ModalTargetType.bulk)) {
                const selectedTabPath = this.commonService.selectedNavigationNode?.navigationInfo?.selectedTab?.path;
                if (selectedTabPath === 'email-thread' || selectedTabPath === 'short-message-thread') {
                    endpoint = Endpoint.viewManager_tagEmailThread;
                    request = {
                        destinationWorkingSetHandle: this.commonService.projectData.handle,
                        tagOperations: [new TagDocumentsOperation({
                            tagList: this.tagApplications,
                            handles: this.targets.map(d => d.handle),
                            comment: this.description,
                        })],
                        taskDescription: this.taskDescription
                    };
                } else {
                    endpoint = Endpoint.viewManager_tagViews;
                    const viewHandleList = this.targets.map(target => target.view ? target.view.handle : (target.corpusViewHandle || target.handle));
                    request = {
                        destinationWorkingSetHandle: this.commonService.projectData ? this.commonService.projectData.handle : this.modalData.handle,
                        viewHandleList: this.modalData.workBasketTaskHandle ? this.modalData.workBasketTaskHandle :
                            (this.commonService.matrixGridsSelection?.openFromDocumentActionBtn ? [this.commonService.matrixGridsSelection?.viewHandle] : viewHandleList),
                        tagList: this.tagApplications,
                        taskDescription: this.taskDescription,
                        comment: this.description
                    };
                }
            } else if (this.targetType === ModalTargetType.document) {
                this.scopeName = this.scope === 'MESSAGE_ATTACHMENT_GROUP' ? 'Family' : 'Thread';
                endpoint = Endpoint.viewManager_tagDocuments;
                request = {
                    destinationWorkingSetHandle: this.commonService.projectData.handle,
                    tagOperations: [new TagDocumentsOperation({
                        tagList: this.tagApplications,
                        handles: this.targets.map(d => d.handle),
                        comment: this.description,
                        scope: this.scope
                    })],
                    taskDescription: this.taskDescription + (this.scopeName = this.scope === 'DOCUMENT_ONLY' ? '' : ' in ' + this.scopeName + ' scope')
                };
            } else if (this.targetType === ModalTargetType.emailThread) {
                endpoint = Endpoint.viewManager_tagEmailThread;
                request = {
                    destinationWorkingSetHandle: this.commonService.projectData.handle,
                    tagOperations: [new TagDocumentsOperation({
                        tagList: this.tagApplications,
                        handles: this.targets.map(d => d.handle),
                        comment: this.description,
                    })],
                    taskDescription: this.taskDescription
                };
            } else if (this.targetType === ModalTargetType.report) {
                endpoint = Endpoint.viewManager_tagViews;
                const viewHandleList = this.targets.map(target => target.viewHandle);
                request = {
                    destinationWorkingSetHandle: this.commonService.projectData.handle,
                    viewHandleList: viewHandleList,
                    tagList: this.tagApplications,
                    taskDescription: this.taskDescription,
                    comment: this.description,
                };
            }
            if (endpoint && request) {
                this.isBusy = true;
                this.isBusyMessage = 'Tagging...';
                this.restService.post(endpoint, request, true, this.updateStatusType ? [MessageType.entityChanged, this.updateStatusType] : true);
                this.isBusy = false;
                this.closeModal(true);
            }
        }
    }

    filterData(str: string): void {
        if (str) {
            this.filteredTags = this.allTags.filter(li => {
                return li.name.toLowerCase().includes(str);
            });
        } else {
            this.filteredTags = this.allTags;
        }
    }

    setScope(scope: TagScope): void {
        this.scope = scope;
    }

    newTag(): void {
        this.commonService.openModal(EditBucketModalComponent, { modalData: { mode: EditMode.TAG } }).then((value) => {
            if (value.modalResult) {
                this.listTags(value.modalData);
            }
        });
    }
}
